import { Box } from '@mui/material';
import { random, useCanvasAnimation } from './useCanvasAnimation';

// HeaderLogo marks its link with this so the umbrella can find the logo without prop drilling
export const HOLIDAY_LOGO_ATTRIBUTE = 'data-holiday-logo';

// scalloped canopy, drawn in a 50 x 22 box
const CANOPY_PATH = 'M2 12 Q25 -8 48 12 Q42 9 37 12 Q31 9 25 12 Q19 9 13 12 Q8 9 2 12Z';
const CANOPY_COLOR = '#2bb3a3';
const DROP_COUNT = 90;

type Canopy = { left: number; right: number; bottom: number; top: number; scale: number };

const measureCanopy = (canvas: HTMLCanvasElement): Canopy | null => {
  const logo = canvas.parentElement?.querySelector(`[${HOLIDAY_LOGO_ATTRIBUTE}]`)?.firstElementChild;
  if (!logo) return null;
  const box = canvas.getBoundingClientRect();
  const rect = logo.getBoundingClientRect();
  const canopyWidth = rect.width + 20;
  const scale = canopyWidth / 50;
  const left = rect.left - box.left - 10;
  const top = Math.max(0, rect.top - box.top - 12 * scale + 4);
  return { left, right: left + canopyWidth, top, bottom: top + 12 * scale, scale };
};

/**
 * National Insurance Awareness Day: rain falls inside the header while an umbrella keeps the LIO logo dry.
 * Render inside a positioned header container.
 */
export const HeaderRain = () => {
  const canvasRef = useCanvasAnimation({
    createStep: ({ width, height }) => {
      const canopyShape = new Path2D(CANOPY_PATH);
      const drops = Array.from({ length: DROP_COUNT }, () => ({
        x: random(0, width),
        y: random(0, height),
        speed: random(0.25, 0.4),
        length: random(5, 10),
      }));

      // the logo can move (resize, scroll-shrink), so re-measure it a couple of times a second
      let canopy: Canopy | null = null;
      let sinceMeasure = Infinity;

      // continuous, so read the live size each frame: drops respawn across the resized header's full width
      return (ctx, dt, { width, height }) => {
        sinceMeasure += dt;
        if (sinceMeasure > 500) {
          canopy = measureCanopy(ctx.canvas);
          sinceMeasure = 0;
        }

        ctx.strokeStyle = 'rgba(170, 200, 230, 0.55)';
        ctx.lineWidth = 1;
        for (const d of drops) {
          d.y += d.speed * dt;
          d.x -= d.speed * dt * 0.15;
          const isUnderUmbrella = !!canopy && d.x > canopy.left && d.x < canopy.right && d.y > canopy.top + 4;
          if (d.y > height + 10 || isUnderUmbrella) {
            d.y = random(-14, -2);
            d.x = random(0, width + 20);
          }
          ctx.beginPath();
          ctx.moveTo(d.x, d.y);
          ctx.lineTo(d.x + d.length * 0.15, d.y - d.length);
          ctx.stroke();
        }

        if (canopy) {
          ctx.save();
          ctx.translate(canopy.left, canopy.top);
          ctx.scale(canopy.scale, canopy.scale);
          ctx.fillStyle = CANOPY_COLOR;
          ctx.fill(canopyShape);
          ctx.fillRect(24.25, -2, 1.5, 4);
          ctx.restore();
        }
      };
    },
  });

  return <Box component='canvas' ref={canvasRef} aria-hidden sx={sxStyles.canvas} />;
};

const sxStyles = {
  canvas: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 5,
  },
};
